import { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faCropSimple } from "@fortawesome/free-solid-svg-icons";
import { Document, Page } from "react-pdf";
import { SnipOverlay, SnipCommentPanel, captureSnippet } from "./annotations";

import Link from "../../atoms/mui/Link";

import { getTaskFileDownloadUrl } from "../../../helpers/firebase";
import "../../../helpers/pdfWorkerSetup";

const BUCKET = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;

const isPdf = ({ mime_type: mimeType, fileName = "" }) =>
  mimeType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf");

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".heic", ".heif"];

const isImage = ({ mime_type: mimeType, fileName = "" }) =>
  (mimeType || "").startsWith("image/") ||
  IMAGE_EXTENSIONS.some((extension) => fileName.toLowerCase().endsWith(extension));

// unsupported_file_type/unsupported_mime_type/document_too_large (and any
// other non-PDF/image) can't be shown in the embedded viewer -- the stored
// object itself may not even be renderable by the browser's iframe/image
// handling, so those rows fall back to a filename + download link using the
// same stored-file URL this panel already resolves.
const isRenderable = (row) => isPdf(row) || isImage(row);

// Page-level "View source", now with ticket #389's snip-and-comment capture
// tool layered on top. Opens the same Storage object the Documents tab
// files, at page {source_page} for PDFs -- rendered via react-pdf's
// <canvas> (not the native iframe PDF viewer) so captureSnippet's domToPng
// can rasterize it; images render via <img>. Both fetch the Storage download
// URL cross-origin, so the bucket's CORS config must allow this app's
// origins, same as the existing report.js image proxy works around.
const SmartUploadSourcePanel = ({
  row,
  line,
  onClose,
  annotation,
  onAnnotationChange,
  onSnipModeChange,
  resetSignal,
  allowAnnotation = true
}) => {
  const { palette } = useTheme();
  const containerRef = useRef(null);
  const [source, setSource] = useState({ gcsPath: null, url: null });
  const [failedPath, setFailedPath] = useState(null);
  const [isSnipping, setIsSnipping] = useState(false);
  const [draftCapture, setDraftCapture] = useState(null);
  const [captureError, setCaptureError] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const { gcsPath, fileName } = row;

  useEffect(() => {
    setPdfLoadError(null);
    getTaskFileDownloadUrl(BUCKET, gcsPath)
      .then((url) => setSource({ gcsPath, url }))
      .catch(() => setFailedPath(gcsPath));
  }, [gcsPath]);

  useEffect(() => {
    onSnipModeChange?.(isSnipping);
  }, [isSnipping, onSnipModeChange]);

  // Mirrors the parent's isSnippingActive back to false if this panel
  // unmounts (e.g. dialog closed) mid-snip, so the parent's Escape-key
  // guard doesn't stay stuck on indefinitely.
  useEffect(() => () => onSnipModeChange?.(false), [onSnipModeChange]);

  // resetSignal is the parent's clientEntryId -- regenerated by
  // resetForAnotherEntry() alongside every other in-progress-entry field,
  // per the spec's Edge Cases. This panel's draft/snip state is local-only
  // and isn't otherwise driven by a prop change, so it needs its own reset.
  useEffect(() => {
    setDraftCapture(null);
    setIsSnipping(false);
    setCaptureError(null);
  }, [resetSignal]);

  const url = source.gcsPath === gcsPath ? source.url : null;
  const pageAnchor =
    isPdf(row) && line?.source_page ? `#page=${line.source_page}` : "";
  const sourceUrl = url ? `${url}${pageAnchor}` : null;

  const startSnip = () => {
    setDraftCapture(null);
    setCaptureError(null);
    setIsSnipping(true);
  };

  const handleSnipComplete = async (rect) => {
    setIsSnipping(false);
    try {
      const thumbnailDataUrl = await captureSnippet({ node: containerRef.current, bbox: rect });
      setDraftCapture({
        rect,
        page: isPdf(row) ? line?.source_page ?? null : null,
        thumbnailDataUrl
      });
    } catch (error) {
      Sentry.captureException(error);
      setCaptureError("We couldn't capture that region. Please try again.");
    }
  };

  const saveDraft = ({ comment, thumbnailDataUrl }) => {
    onAnnotationChange({ ...draftCapture, comment, thumbnailDataUrl });
    setDraftCapture(null);
  };

  const canSnip =
    allowAnnotation && sourceUrl && isRenderable(row) && !annotation && !draftCapture && !pdfLoadError;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "45%",
        minWidth: 320,
        pl: 2,
        borderLeft: `1px solid ${palette.backgroundGray.dark}`
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2">{fileName}</Typography>
          {line?.source_page && (
            <Typography variant="caption" color="textSecondary">
              Page {line.source_page}
            </Typography>
          )}
        </Box>
        <IconButton size="small" aria-label="Close source" onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </Box>
      {failedPath === gcsPath && !sourceUrl && (
        <Typography variant="body2" color="error">
          We couldn&apos;t open this file.
        </Typography>
      )}
      {!sourceUrl && failedPath !== gcsPath && <CircularProgress size={20} />}
      {sourceUrl && isRenderable(row) && (
        <>
          <Box
            ref={containerRef}
            sx={{
              position: "relative",
              flexGrow: 1,
              minHeight: 500,
              overflow: "auto",
              border: `1px solid ${palette.backgroundGray.dark}`
            }}
          >
            {isPdf(row) && !pdfLoadError && (
              <Document
                file={url}
                loading={<CircularProgress size={20} />}
                onLoadError={(error) => {
                  Sentry.captureException(error);
                  setPdfLoadError(error);
                }}
              >
                <Page
                  pageNumber={line?.source_page || 1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            )}
            {isPdf(row) && pdfLoadError && (
              <Typography variant="body2" color="error" sx={{ p: 2 }}>
                We couldn&apos;t open this PDF for preview. Use &quot;Open in a new tab&quot; below instead.
              </Typography>
            )}
            {!isPdf(row) && (
              <Box component="img" src={sourceUrl} alt={fileName} sx={{ width: "100%", display: "block" }} />
            )}
            {isSnipping && (
              <SnipOverlay
                containerRef={containerRef}
                onComplete={handleSnipComplete}
                onCancel={() => setIsSnipping(false)}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Link href={sourceUrl}>
              <Typography variant="caption">Open in a new tab</Typography>
            </Link>
            {canSnip && (
              <Button
                size="small"
                startIcon={<FontAwesomeIcon icon={faCropSimple} />}
                onClick={startSnip}
              >
                Snip &amp; comment
              </Button>
            )}
          </Box>
          {captureError && (
            <Typography variant="body2" color="error">
              {captureError}
            </Typography>
          )}
          {draftCapture && (
            <SnipCommentPanel
              thumbnailDataUrl={draftCapture.thumbnailDataUrl}
              onSave={saveDraft}
              onDiscard={() => setDraftCapture(null)}
            />
          )}
          {annotation && !draftCapture && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                p: 1,
                border: `1px solid ${palette.backgroundGray.dark}`,
                borderRadius: 1
              }}
            >
              <Box
                component="img"
                src={annotation.thumbnailDataUrl}
                alt="Flagged region"
                sx={{ width: 80, borderRadius: 1 }}
              />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {annotation.comment}
              </Typography>
              <IconButton
                size="small"
                aria-label="Remove flagged region"
                onClick={() => onAnnotationChange(null)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </Box>
          )}
        </>
      )}
      {sourceUrl && !isRenderable(row) && (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: `1px solid ${palette.backgroundGray.dark}`
          }}
        >
          <Typography variant="body2" color="textSecondary">
            This file can&apos;t be previewed here.
          </Typography>
          <Link href={sourceUrl}>
            <Typography variant="body2">Download {fileName}</Typography>
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default SmartUploadSourcePanel;
